import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Clock, Timer, ChevronDown, Zap } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useHaptic } from '@/hooks/useHaptic';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface AddTaskFormProps {
  onAdd: (content: string, duration?: number, dueTime?: string) => Promise<void>;
  onAddSideQuest?: (content: string, duration?: number, dueTime?: string) => Promise<void>;
  placeholder?: string;
  compact?: boolean;
  autoFocus?: boolean;
  preventMobileFocus?: boolean;
}

const DURATION_OPTIONS = [
  { label: '15m', value: 15 },
  { label: '30m', value: 30 },
  { label: '1h', value: 60 },
  { label: '2h', value: 120 },
  { label: '4h', value: 240 },
];

export function AddTaskForm({ onAdd, onAddSideQuest, placeholder = "Add a task...", compact = false, autoFocus = false, preventMobileFocus = false }: AddTaskFormProps) {
  const [content, setContent] = useState('');
  const [duration, setDuration] = useState<number | undefined>();
  const [dueTime, setDueTime] = useState<string | undefined>();
  const [showOptions, setShowOptions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();
  const { triggerError, triggerSuccess } = useHaptic();
  
  // Prevent auto-focus on mobile when preventMobileFocus is set
  useEffect(() => {
    if (preventMobileFocus && isMobile && inputRef.current) {
      // Blur if focused to prevent keyboard appearing
      inputRef.current.blur();
    }
  }, [preventMobileFocus, isMobile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting) {
      if (!content.trim()) {
        triggerError(); // Haptic error for empty task
      }
      return;
    }

    setIsSubmitting(true);
    await onAdd(content.trim(), duration, dueTime);
    triggerSuccess(); // Haptic success for task added
    setContent('');
    setDuration(undefined);
    setDueTime(undefined);
    setShowOptions(false);
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2" data-tour-target="add-task-form">
      <div className="relative flex items-center gap-2">
        <div className="relative flex-1">
          <label htmlFor="add-task-input" className="sr-only">
            Add task
          </label>
          <Input
            id="add-task-input"
            ref={inputRef}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus && !isMobile}
            className={cn(
              "h-11 pl-10 pr-4 bg-card border-border/50 rounded-xl",
              "focus:ring-2 focus:ring-primary/20 focus:border-primary",
              "placeholder:text-muted-foreground/50"
            )}
          />
          <Plus className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/50" />
        </div>

        {/* Side Quest Button */}
        {onAddSideQuest && (
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label="Add as side quest"
                  onClick={async () => {
                    if (!content.trim() || isSubmitting) return;
                    setIsSubmitting(true);
                    await onAddSideQuest(content.trim(), duration, dueTime);
                    setContent('');
                    setDuration(undefined);
                    setDueTime(undefined);
                    setShowOptions(false);
                    setIsSubmitting(false);
                  }}
                  disabled={!content.trim() || isSubmitting}
                  className={cn(
                    "h-11 w-11 rounded-xl border-[hsl(var(--side-quest))]/30",
                    "text-[hsl(var(--side-quest))] hover:bg-[hsl(var(--side-quest))]/10 hover:border-[hsl(var(--side-quest))]/50",
                    "disabled:opacity-30"
                  )}
                >
                  <Zap className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Add as Side Quest</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={() => setShowOptions(!showOptions)}
          className={cn(
            "h-11 w-11 rounded-xl",
            showOptions && "bg-accent"
          )}
        >
          <ChevronDown className={cn(
            "h-4 w-4 transition-transform",
            showOptions && "rotate-180"
          )} />
        </Button>
      </div>

      <AnimatePresence>
        {showOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="flex flex-wrap gap-2 pt-2">
              {/* Duration selector */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 rounded-lg gap-1.5",
                      duration && "bg-accent border-accent"
                    )}
                  >
                    <Timer className="h-3.5 w-3.5" />
                    {duration ? `${duration >= 60 ? `${duration / 60}h` : `${duration}m`}` : 'Duration'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2" align="start">
                  <div className="flex gap-1">
                    {DURATION_OPTIONS.map((opt) => (
                      <Button
                        key={opt.value}
                        type="button"
                        variant={duration === opt.value ? "default" : "ghost"}
                        size="sm"
                        className="h-7 px-2.5"
                        onClick={() => setDuration(duration === opt.value ? undefined : opt.value)}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Time picker */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 rounded-lg gap-1.5",
                      dueTime && "bg-accent border-accent"
                    )}
                  >
                    <Clock className="h-3.5 w-3.5" />
                    {dueTime || 'Set time'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" align="start">
                  <Input
                    type="time"
                    value={dueTime || ''}
                    onChange={(e) => setDueTime(e.target.value || undefined)}
                    className="w-32"
                  />
                  {dueTime && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full mt-2"
                      onClick={() => setDueTime(undefined)}
                    >
                      Clear
                    </Button>
                  )}
                </PopoverContent>
              </Popover>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {(content.trim() || compact) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <Button
            type="submit"
            disabled={isSubmitting || !content.trim()}
            className="w-full h-10 rounded-xl"
          >
            Add Task
          </Button>
        </motion.div>
      )}
    </form>
  );
}
